import { ApiClient } from '@/shared/api-client/api-client';
import { IPagedList } from '@/shared/api-client/models';
import { IMyMotionsListEntry } from '@/shared/api-client/models/motion/my-motions-list-entry';
import { friendlyDateTime } from '@/shared/filters/date-time-filter';
import { ArgumentValidator } from '@/shared/helpers/argument-validator';
import { StringComparer } from '@/shared/helpers/string-comparer';
import { UIEventHandler } from '@/shared/helpers/ui-event-handler';
import { IPagination } from '@/shared/Models/pagination';
import { Component, Prop, Vue } from 'vue-property-decorator';
import MyMotionsStatusViewerComponent from './my-motions-status-viewer-component/my-motions-status-viewer-component.vue';
import MyMotionsDetailsComponent from '../my-motions-details-component/my-motions-details-component.vue';
import { NameOf } from '@/shared/helpers';

interface IViewModel {
    pagedItems: IPagedList<IMyMotionsListEntry>;
    pagination: IPagination;
    isInitialized: boolean;
    isLoading: boolean;
    motion?: IMyMotionsListEntry;
}

@Component({
    computed: {
        StringComparer() {
            return StringComparer
        }
    },
    components: {
        MyMotionsStatusViewerComponent,
        MyMotionsDetailsComponent
    },
    filters: {
        friendlyDateTime
    }
})
export default class MyMotionsComponent extends Vue {

    @Prop({ default: 'sv-SE' }) public culture!: string;
    @Prop({ default: false }) public allowUserResubmit!: boolean;
    @Prop() public translations!: { [key: string]: string; };

    public async created() {
        await this.refreshList();
    }

    private apiClient: ApiClient = new ApiClient();

    public viewModel: IViewModel = {
        pagedItems: {
            items: [],
            totalNbrOfItems: 0
        },
        pagination: {
            page: 1,
            pageSize: 15,
            total: -1
        },
        isInitialized: false,
        isLoading: false,
    }

    public get mode() : 'List' | 'Details' {
        return this.viewModel.motion ? 'Details' : 'List';
    }

    public get anyMotionCanBeResubmitted(): boolean {

        if (!this.viewModel?.pagedItems?.items) {
            return false;
        }

        return this.viewModel.pagedItems.items.some(x => x.canBeResubmitted);
    }

    public printProperty(motion: IMyMotionsListEntry, propertyIdentifier: string, defaultValue = 'N/A'): string {

        ArgumentValidator.notNullOrEmpty(motion, `motion`);
        ArgumentValidator.notNullOrEmpty(propertyIdentifier, `propertyName`);

        return motion.properties.find(prop => prop.identifier === propertyIdentifier)?.value || defaultValue;
    }

    public async pageChanged(page: number): Promise<void> {

        this.viewModel.pagination.page = page;

        await this.refreshList();
    }

    public async resubmit(motion: IMyMotionsListEntry, $event: UIEvent): Promise<void> {

        UIEventHandler.preventDefaults($event);

        if (!this.allowUserResubmit) {
            return;
        }

        if (!confirm(this.translations['general.confirm'])) {
            return;
        }

        await this.apiClient.motion.resubmit(motion.activityId, motion.id);

        this.refreshList();
    }

    public motionDetails(motion: IMyMotionsListEntry, $event: UIEvent): void {

        ArgumentValidator.notNullOrEmpty(motion, `motion`);
        UIEventHandler.preventDefaults($event);

        this.$set(this.viewModel, NameOf.property(this.viewModel, x => x.motion), motion);
    }

    public async deleteMotion(motion: IMyMotionsListEntry, $event: UIEvent): Promise<void> {

        UIEventHandler.preventDefaults($event);

        if (!confirm(this.translations['general.confirm'])) {
            return;
        }

        await this.apiClient.motion.delete(motion.activityId, motion.id);

        this.refreshList();
    }

    private async refreshList(): Promise<void> {

        try {

            this.viewModel.isLoading = true;

            this.viewModel.pagedItems = await this.apiClient.motion.listMyMotions(
                this.viewModel.pagination
            );

            this.viewModel.pagination.total = this.viewModel.pagedItems.totalNbrOfItems;

            this.viewModel.isInitialized = true;

        } finally {

            this.viewModel.isLoading = false;
        }
    }
}